<template>
  <DropdownModule
    ref="dropdownRef"
    class="flex"
    :class="{'opacity-100': showMenu || hasReactions}"
    :width="139"
    append-to-body
    dropdown-z-index="z-100"
    position-menu="left"
    :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
    @click="showMenu = true"
    @close="showMenu = false"
  >
    <template #button="{isOpen}">
      <div
        class="cursor-pointer flex items-center"
        :class="isOpen ? 'opacity-100' : ''"
      >
        <div v-if="hasReactions" class="flex">
          <div
            v-for="(key, value, index) in parsedReactions"
            :key="value + key + index"
            class="text-brand flex items-center border border-brand bg-brand bg-opacity-10 rounded-4 mr-5 py-5 px-7 group-scope relative"
            @mouseenter="toggleTooltip(true, value)"
            @mouseleave="toggleTooltip(false)"
            @click.stop="toggleReaction(value)"
          >
            <i class="inline-block text-18 rounded-10 anim-all mr-7" :class="`icon-reaction-${value}`" />
            <p class="text-12 leading-none">{{ key }}</p>
            <!-- tooltip -->
            <ReactionTooltip
              :id="+value"
              :message-id="messageId"
              class="hidden group-scope-hover:block"
              :message-reactions="messageReactions"
            />
          </div>
        </div>

        <i
          v-if="!usersReaction"
          class="md:opacity-0 group-hover:opacity-100 inline-block icon-reaction-1 text-18 p-6 rounded-8 anim-all"
          :class="classes"
          @click.stop="openDropdown"
        />
      </div>
    </template>

    <div class="rounded-10 text-app-color p-5">
      <i
        v-for="reaction in reactions"
        :key="reaction.id"
        class="cursor-pointer text-brand text-18 p-7 inline-block rounded-8"
        :class="[`icon-reaction-${reaction.id}`, isDarkMode ? 'hover:bg-project' : 'hover:bg-grey-400']"
        @click="selectReaction(reaction)"
      />
    </div>
  </DropdownModule>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'

import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ReactionTooltip from '@/components/discussions/ReactionTooltip.vue'

export default defineComponent({
  components: { DropdownModule, ReactionTooltip },

  props: {
    messageId: Number,
    bookId: Number,
    discussionId: Number,
    messageReactions: {
      required: true,
      type: Array
    }
  },

  setup (props) {
    const { state, getters, dispatch } = useStore()

    const dropdownRef = ref(null)

    const showMenu = ref(false)
    const showTooltip = ref(false)

    const user = computed(() => state.profile.user)

    const classes = computed(() => {
      const prefix = showMenu.value ? '' : 'text-grey-250 hover:'
      const bg = isDarkMode.value ? 'bg-project' : 'bg-grey-400'

      return `${prefix}${bg} ${prefix}text-brand `
    })

    const hasReactions = computed(() => props.messageReactions?.length > 0)

    const usersReaction = computed(() => props.messageReactions?.find(item => item.userId === user.value.id))

    const parsedReactions = computed(() => {
      if (hasReactions.value) {
        return props.messageReactions?.reduce(function (acc, curr) {
          acc[curr.reactionId] ? ++acc[curr.reactionId] : acc[curr.reactionId] = 1
          return acc
        }, {})
      } else {
        return []
      }
    })

    const users = computed(() => id => {
      return props.messageReactions?.filter(item => item.id === id).map(item => item.user)
    })

    const reactions = computed(() => state.discussions.reactions)

    const isDarkMode = computed(() => getters.isDarkMode)
    const isMobile = computed(() => getters.isMobile)

    function toggleReaction (id) {
      if (usersReaction.value && +usersReaction.value.reactionId !== +id) return

      if (+usersReaction.value?.reactionId === +id) {
        dispatch('toggleReaction', {
          messageId: props.messageId,
          reactionId: id,
          bookId: props.bookId,
          discussionId: props.discussionId,
          user: user.value,
          userId: user.value.id,
          isSelected: true
        })
      }
    }

    function selectReaction (item) {
      dispatch('toggleReaction', {
        messageId: props.messageId,
        reactionId: item.id,
        bookId: props.bookId,
        discussionId: props.discussionId,
        user: user.value,
        userId: user.value.id,
        isSelected: false
      })
    }

    function openDropdown () {
      dropdownRef.value.toggle()
    }

    function toggleTooltip (value, key) {
      showTooltip.value = value
    }

    return {
      // ref
      dropdownRef,
      // data
      showMenu,
      showTooltip,
      // computed
      users,
      classes,
      isMobile,
      reactions,
      isDarkMode,
      hasReactions,
      usersReaction,
      parsedReactions,
      // function
      openDropdown,
      toggleTooltip,
      selectReaction,
      toggleReaction
    }
  }
})
</script>
